import {useRouting} from '@/composables/use-routing.js'
import {useLoginUrl} from '@/composables/use-login-url.js'
import {useTeams} from '@/composables/use-teams.js'
import {useAuthentication} from '@/composables/use-authentication.js'
import {useAuth0} from '@/composables/use-auth0.js'
import {useEnv} from '@/composables/use-env.js'
import {useCurrentUser} from '@/composables/use-current-user.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useSegment} from '@/composables/use-segment.js'
import {useNavigation} from '@/composables/use-navigation.js'

//True when mount should start interactive Auth0 login and defer the rest of the mount logic
const isAuth0InteractiveRedirectError = error =>
  error?.message === 'Auth0 ID token unavailable' ||
  error?.error === 'login_required' ||
  error?.error === 'consent_required'

/**
 * App mount logic
 */
export function useAppMount() {

  //Composables
  const {router} = useRouting()
  const {env, origin, gitHash} = useEnv()
  const {captureLoginUrlFromQuery} = useLoginUrl()
  const {initializeTeams, isInTeams, deepLink} = useTeams()
  const {initializeAuth0, handleAuth0Callback, loginWithAuth0, hasAuth0RedirectCallbackParams} = useAuth0()
  const {loadUser, user} = useCurrentUser()
  const {loadOrganization, organization} = useCurrentOrganization()
  const {identify} = useSegment()
  const {followTeamsDeepLink} = useNavigation()

  //Auth
  const {
    authenticateWithTeams,
    setAuthenticated,
    obtainCsrfToken,
    syncAuth0Cookie,
    registerReAuthenticationHandler,
  } = useAuthentication()

  //Helpers to create ready or deferred outcomes
  const readyOutcome = () => ({outcome: 'ready'})
  const deferredOutcome = reason => ({outcome: 'deferred', reason})

  //Helper to load resources
  const loadUserAndOrganization = async () => {

    //Already loaded
    if (user.value && organization.value) {
      return
    }

    //Load both in parallel now
    await Promise.all([
      loadUser(),
      loadOrganization(),
    ])
  }

  //Helper to setup analytics post auth
  const setupAnalytics = () => {
    identify()
  }

  //Helper to authenticate a Teams user
  const authenticateTeamsUser = async () => {

    //Wrapping in a separate try block here, because we want any error that the
    //API responds with to be treated as an authentication error, rather than a
    //generic server error. The backend will in some cases, especially with bad
    //data, respond with a 500 instead, which results to the user seeing the
    //generic error page, rather than the auth specific one.
    try {
      await authenticateWithTeams()
    }

    //Force status to 401 and rethrow, to handle it properly in the parent catch block
    catch (error) {
      error.status = 401
      throw error
    }
  }

  //Helper to authenticate a web user
  const authenticateWebUser = async () => {

    //Initialize Auth0 and handle callback if returning from Auth0 login
    await initializeAuth0()
    await handleAuth0Callback()

    //Sync Auth0 cookie (silent if session exists, redirect to Auth0 if not)
    //If a redirect was initiated, stop here
    try {
      await syncAuth0Cookie()
    }
    catch (error) {
      if (isAuth0InteractiveRedirectError(error)) {
        loginWithAuth0()
        return deferredOutcome('auth0_redirect')
      }
      throw error
    }
  }

  //Helper to try and authenticate a web user with cookies
  const authenticateWebUserWithCookies = async () => {

    //Try optimistically loading user and organization early to see
    //if we're already authenticated via cookies
    try {
      await loadUserAndOrganization()
    }
    catch (error) {

      //Rethrow error if it's not an authentication error
      if (!error.isUnauthenticatedError) {
        throw error
      }

      //Authenticate web user
      const result = await authenticateWebUser()
      if (result?.outcome === 'deferred') {
        return result
      }
    }
  }

  //Teams app mount logic
  const mountAppForTeams = async () => {

    //Authenticate teams user and load data
    await authenticateTeamsUser()
    await loadUserAndOrganization()

    //Setup analytics and register re-authentication handler
    setupAnalytics()
    registerReAuthenticationHandler()

    //Follow deep link if present
    if (deepLink.value) {
      followTeamsDeepLink(deepLink.value)
    }

    //Ready to go
    return readyOutcome()
  }

  //Web app mount logic
  const mountAppForWeb = async () => {

    //Determine auth handler to use, based on whether we have Auth0 redirect callback params
    const authHandler = hasAuth0RedirectCallbackParams.value ?
      authenticateWebUser :
      authenticateWebUserWithCookies

    //Call auth handler
    const result = await authHandler()
    if (result?.outcome === 'deferred') {
      return result
    }

    //Flag user as authenticated
    setAuthenticated(true)

    //Load data and CSRF token
    await loadUserAndOrganization()
    await obtainCsrfToken()

    //Setup analytics and register re-authentication handler
    setupAnalytics()
    registerReAuthenticationHandler()

    //Ready to go
    return readyOutcome()
  }

  //App mount logic
  const mountApp = async () => {

    //Log some environment info
    if (typeof process === 'undefined' || !process.env.VITEST) {
      console.log(`---------------------------------------------------------------`)
      console.log(`Running in ${env.value} environment on ${origin.value} 🚀`)
      console.log(`Version: ${gitHash.value}`)
      console.log(`---------------------------------------------------------------`)
    }

    //Wait for router to be ready (so that query params are available)
    await router.isReady()

    //Capture messenger login URL from entry link query parameters if present
    await captureLoginUrlFromQuery()

    //This has to be done before authentication so that we can establish whether
    //we're running in Teams or not, which has an impact on the authentication flow
    await initializeTeams()

    //Mount rest of app depending on whether we're in Teams or not
    if (isInTeams.value) {
      return await mountAppForTeams()
    }
    else {
      return await mountAppForWeb()
    }
  }

  //Expose interface
  return {
    mountApp,
  }
}
